import { inject, Injectable, signal } from '@angular/core';
import {
  Auth,
  AuthError,
  EmailAuthProvider,
  GoogleAuthProvider,
  isSignInWithEmailLink,
  linkWithCredential,
  OAuthCredential,
  reauthenticateWithCredential,
  sendSignInLinkToEmail,
  signInWithEmailAndPassword,
  signInWithEmailLink,
  signInWithPopup,
  signOut,
  updatePassword,
  user,
  User as FirebaseUser,
} from '@angular/fire/auth';
import { getAuth as getFirebaseAuth, createUserWithEmailAndPassword } from 'firebase/auth';
import { initializeApp, getApps } from 'firebase/app';
import {
  addDoc,
  collection,
  collectionGroup,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  limit,
  query,
  setDoc,
  where,
  writeBatch,
  Firestore,
} from '@angular/fire/firestore';
import { Clinic } from '../models/clinic.model';
import { Router } from '@angular/router';
import { environment } from '../../../environments/environment';
import { toSignal } from '@angular/core/rxjs-interop';
import { ClinicService } from './clinic.service';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly auth = inject(Auth);
  private readonly firestore = inject(Firestore);
  private readonly router = inject(Router);
  private readonly clinicService = inject(ClinicService);

  readonly currentUser = toSignal(user(this.auth), { initialValue: null });
  readonly loading = signal(false);
  readonly error = signal<string | null>(null);
  readonly notAdmin = signal(false);
  readonly suspended = signal(false);
  readonly pendingGoogleLink = signal<{ credential: OAuthCredential; email: string } | null>(null);

  isEmailSignInLink(url: string): boolean {
    return isSignInWithEmailLink(this.auth, url);
  }

  async signup(clinicName: string, email: string, password: string, practiceType: 'solo' | 'clinic' = 'clinic'): Promise<void> {
    this.loading.set(true);
    this.error.set(null);
    try {
      const cred = await createUserWithEmailAndPassword(this.auth, email, password);
      const uid = cred.user.uid;
      const now = new Date().toISOString();
      const clinicRef = doc(collection(this.firestore, 'clinics'));
      const clinicId = clinicRef.id;
      const batch = writeBatch(this.firestore);
      batch.set(clinicRef, {
        id: clinicId,
        name: clinicName,
        type: practiceType,
        primaryColor: '#2d6a4f',
        logoUrl: null,
        createdAt: now,
        trialStartedAt: now,
        subscriptionStatus: 'trial',
      });
      batch.set(doc(this.firestore, 'clinics', clinicId, 'users', uid), {
        uid, clinicId, email, role: 'owner', status: 'active',
        isDoctor: practiceType === 'solo',
        createdAt: now,
      });
      batch.set(doc(this.firestore, 'userClinics', uid), {
        clinicId, role: 'owner', status: 'active', email,
      });
      batch.set(doc(this.firestore, 'userEmailIndex', email), {
        clinicId, role: 'owner', status: 'active',
      });
      await batch.commit();
      await setDoc(doc(this.firestore, 'verificationRequests', uid), { uid, createdAt: now });
      this.clinicService.setClinic({
        id: clinicId, name: clinicName, primaryColor: '#2d6a4f',
        type: practiceType,
        trialStartedAt: now, subscriptionStatus: 'trial',
      });
      this.clinicService.setRole('owner');
      await this.router.navigateByUrl('/');
    } catch (err) {
      this.error.set(this.parseError(err));
    } finally {
      this.loading.set(false);
    }
  }

  async createUserAccount(email: string, password: string): Promise<string | null> {
    const SECONDARY = 'invite-creator';
    const secondary = getApps().find((a) => a.name === SECONDARY)
      ?? initializeApp(environment.firebase, SECONDARY);
    const secondaryAuth = getFirebaseAuth(secondary);
    try {
      const cred = await createUserWithEmailAndPassword(secondaryAuth, email, password);
      const uid = cred.user.uid;
      await secondaryAuth.signOut();
      return uid;
    } catch (err) {
      if (
        err && typeof err === 'object' && 'code' in err &&
        (err as { code: string }).code === 'auth/email-already-in-use'
      ) {
        return null;
      }
      throw err;
    }
  }

  async sendInviteEmail(email: string): Promise<void> {
    await sendSignInLinkToEmail(this.auth, email, {
      url: `${window.location.origin}/login`,
      handleCodeInApp: true,
    });
    localStorage.setItem('emailForSignIn', email);
  }

  async loginWithEmailLink(email: string, emailLink: string): Promise<void> {
    this.loading.set(true);
    this.error.set(null);
    this.notAdmin.set(false);
    this.suspended.set(false);
    try {
      const cred = await signInWithEmailLink(this.auth, email, emailLink);
      localStorage.removeItem('emailForSignIn');
      await this.afterLogin(cred.user);
    } catch (err) {
      this.error.set(this.parseError(err));
    } finally {
      this.loading.set(false);
    }
  }

  async loginWithEmail(email: string, password: string): Promise<void> {
    this.loading.set(true);
    this.error.set(null);
    this.notAdmin.set(false);
    this.suspended.set(false);
    try {
      const cred = await signInWithEmailAndPassword(this.auth, email, password);
      await this.afterLogin(cred.user);
    } catch (err) {
      this.error.set(this.parseError(err));
    } finally {
      this.loading.set(false);
    }
  }

  async loginWithGoogle(): Promise<void> {
    this.loading.set(true);
    this.error.set(null);
    this.notAdmin.set(false);
    this.suspended.set(false);
    try {
      const cred = await signInWithPopup(this.auth, new GoogleAuthProvider());
      await this.afterLogin(cred.user);
    } catch (err: unknown) {
      const authErr = err as AuthError;
      if (authErr?.code === 'auth/account-exists-with-different-credential') {
        // Email already has an email/password account. Store the Google credential
        // so the user can enter their password to link both providers in one account.
        const credential = GoogleAuthProvider.credentialFromError(authErr);
        const email = authErr.customData?.email as string | undefined;
        if (credential && email) {
          this.pendingGoogleLink.set({ credential, email });
          return;
        }
      }
      this.error.set(this.parseError(err));
    } finally {
      this.loading.set(false);
    }
  }

  async completeGoogleLink(password: string): Promise<void> {
    const pending = this.pendingGoogleLink();
    if (!pending) return;
    this.loading.set(true);
    this.error.set(null);
    try {
      const cred = await signInWithEmailAndPassword(this.auth, pending.email, password);
      await linkWithCredential(cred.user, pending.credential);
      this.pendingGoogleLink.set(null);
      await this.afterLogin(cred.user);
    } catch (err) {
      this.error.set(this.parseError(err));
    } finally {
      this.loading.set(false);
    }
  }

  cancelGoogleLink(): void {
    this.pendingGoogleLink.set(null);
    this.error.set(null);
  }

  async resendVerificationEmail(): Promise<void> {
    const u = this.auth.currentUser;
    if (u && !u.emailVerified) {
      await setDoc(doc(this.firestore, 'verificationRequests', u.uid), {
        uid: u.uid,
        createdAt: new Date().toISOString(),
      });
    }
  }

  async reloadUser(): Promise<boolean> {
    const u = this.auth.currentUser;
    if (!u) return false;
    await u.reload();
    const verified = this.auth.currentUser?.emailVerified ?? false;
    if (verified) {
      const clinicId = this.clinicService.clinic()?.id;
      if (clinicId) {
        await setDoc(
          doc(this.firestore, 'clinics', clinicId, 'users', u.uid),
          { emailVerified: true },
          { merge: true },
        );
      }
    }
    return verified;
  }

  async sendPasswordReset(email: string): Promise<void> {
    await addDoc(collection(this.firestore, 'passwordResetRequests'), {
      email,
      createdAt: new Date().toISOString(),
    });
  }

  async changePassword(currentPassword: string, newPassword: string): Promise<void> {
    const firebaseUser = this.auth.currentUser;
    if (!firebaseUser?.email) throw new Error('No authenticated user.');
    const credential = EmailAuthProvider.credential(firebaseUser.email, currentPassword);
    await reauthenticateWithCredential(firebaseUser, credential);
    await updatePassword(firebaseUser, newPassword);
  }

  async logout(): Promise<void> {
    this.notAdmin.set(false);
    this.suspended.set(false);
    this.error.set(null);
    this.clinicService.clear();
    await signOut(this.auth);
    await this.router.navigateByUrl('/login');
  }

  private async afterLogin(firebaseUser: FirebaseUser): Promise<void> {
    const uid = firebaseUser.uid;

    // 1. SaaS manager check
    const mgrSnap = await getDoc(doc(this.firestore, 'saasManagers', uid));
    if (mgrSnap.exists()) {
      await this.router.navigateByUrl('/manager');
      return;
    }

    // 2. Existing clinic user — direct lookup (no collection-group query needed)
    const ucSnap = await getDoc(doc(this.firestore, 'userClinics', uid));
    if (ucSnap.exists()) {
      const uc = ucSnap.data() as { clinicId: string; role: string; status: string };
      if (uc.status === 'suspended') {
        this.suspended.set(true);
        return;
      }
      const clinicSnap = await getDoc(doc(this.firestore, 'clinics', uc.clinicId));
      if (clinicSnap.exists()) {
        const c = clinicSnap.data() as Clinic;
        this.clinicService.setClinic({ id: c.id, name: c.name, primaryColor: c.primaryColor, logoUrl: c.logoUrl, type: c.type, trialStartedAt: c.trialStartedAt, subscriptionStatus: c.subscriptionStatus });
        this.clinicService.setRole(uc.role);
      }
      await this.router.navigateByUrl('/');
      return;
    }

    const email = firebaseUser.email ?? '';

    // 2.5 Email-indexed access — supports multiple sign-in methods (email/password + Google)
    // for the same email address. Written when any UID first accepts an invite.
    if (email) {
      const eiSnap = await getDoc(doc(this.firestore, 'userEmailIndex', email));
      if (eiSnap.exists()) {
        const ei = eiSnap.data() as { clinicId: string; role: string; status: string };
        if (ei.status === 'suspended') {
          this.suspended.set(true);
          return;
        }
        const clinicSnap = await getDoc(doc(this.firestore, 'clinics', ei.clinicId));
        if (clinicSnap.exists()) {
          const c = clinicSnap.data() as Clinic;
          // Create userClinics for this new sign-in method (same email, different UID)
          await setDoc(doc(this.firestore, 'userClinics', uid), {
            clinicId: ei.clinicId,
            role: ei.role,
            status: ei.status,
            email,
          });
          this.clinicService.setClinic({ id: c.id, name: c.name, primaryColor: c.primaryColor, logoUrl: c.logoUrl, type: c.type, trialStartedAt: c.trialStartedAt, subscriptionStatus: c.subscriptionStatus });
          this.clinicService.setRole(ei.role);
          await this.router.navigateByUrl('/');
          return;
        }
      }
    }

    // 3. Pending clinic invite — direct lookup by email (no collection-group query needed)
    if (email) {
      const pendingSnap = await getDoc(doc(this.firestore, 'pendingInvites', email));
      if (pendingSnap.exists()) {
        const pending = pendingSnap.data() as {
          clinicId: string; inviteId: string; role: string; email: string;
        };
        // Validate the invite still exists — clinic admin may have cancelled it
        // after pendingInvites was written. If gone, clean up the stale doc and deny.
        const inviteSnap = await getDoc(doc(this.firestore, 'clinics', pending.clinicId, 'invites', pending.inviteId));
        if (!inviteSnap.exists()) {
          deleteDoc(doc(this.firestore, 'pendingInvites', email)).catch(() => {});
          this.notAdmin.set(true);
          return;
        }
        const clinicSnap = await getDoc(doc(this.firestore, 'clinics', pending.clinicId));
        if (clinicSnap.exists()) {
          const c = clinicSnap.data() as Clinic;
          const batch = writeBatch(this.firestore);
          batch.set(doc(this.firestore, 'clinics', pending.clinicId, 'users', uid), {
            uid,
            clinicId: pending.clinicId,
            email,
            role: pending.role,
            status: 'active',
            createdAt: new Date().toISOString(),
          });
          batch.set(doc(this.firestore, 'userClinics', uid), {
            clinicId: pending.clinicId,
            role: pending.role,
            status: 'active',
            email,
          });
          batch.set(doc(this.firestore, 'userEmailIndex', email), {
            clinicId: pending.clinicId,
            role: pending.role,
            status: 'active',
          });
          batch.delete(doc(this.firestore, 'clinics', pending.clinicId, 'invites', pending.inviteId));
          batch.delete(doc(this.firestore, 'pendingInvites', email));
          await batch.commit();
          this.clinicService.setClinic({ id: c.id, name: c.name, primaryColor: c.primaryColor, logoUrl: c.logoUrl, type: c.type, trialStartedAt: c.trialStartedAt, subscriptionStatus: c.subscriptionStatus });
          this.clinicService.setRole(pending.role);
          await this.router.navigateByUrl('/');
          return;
        }
      }
    }

    // 4. No access
    this.notAdmin.set(true);
  }

  private parseError(err: unknown): string {
    if (err && typeof err === 'object' && 'code' in err) {
      const code = (err as { code: string }).code;
      const messages: Record<string, string> = {
        'auth/invalid-credential': 'Invalid email or password.',
        'auth/user-not-found': 'No account found with this email.',
        'auth/wrong-password': 'Incorrect password.',
        'auth/too-many-requests': 'Too many attempts. Please try again later.',
        'auth/popup-closed-by-user': 'Sign-in popup was closed.',
        'auth/account-exists-with-different-credential': 'This email is already registered with email/password. Please sign in using your email and password instead.',
        'auth/network-request-failed': 'Network error. Check your connection.',
        'auth/invalid-action-code': 'This invite link is invalid or has already been used.',
        'auth/expired-action-code': 'This invite link has expired. Request a new invitation.',
        'auth/invalid-email': 'The email address does not match the invitation.',
        'permission-denied': 'Access denied. You may not have permission to perform this action.',
        'failed-precondition': 'A required database configuration is missing. Please contact support.',
        'unavailable': 'Service temporarily unavailable. Please try again.',
      };
      return messages[code] ?? `An unexpected error occurred (${code}).`;
    }
    return 'An unexpected error occurred.';
  }
}
